import React, { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "config/axiosConfig";
import { Carousel, CarouselContent, CarouselItem } from "ui/Carousel";
import EntityAvatar from "components/Entities/EntityAvatar";
import EntitySubscription from "ui/EntitySubscription";
import { useUser } from "util/UserContext";
import { Issue } from "util/constants";
import { getIssueRankings, IssueRanking } from "util/userUtils";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import LoadingSkeleton from "ui/LoadingSkeleton";
import SubscriptionModal from "./SubscriptionModal";
import UserStoryAvatar, { type UserStoryRailEntry } from "./UserStoryAvatar";
import { apiRoutes } from "util/apiRoutes";

interface SubscriptionRelation {
  rel_node_type: string;
  rel_node_id: number;
  rel_type: string;
  properties?: {
    value?: number;
    privacy?: string;
  };
}

interface SubscriptionResponse {
  relations: SubscriptionRelation[];
}

interface IssueEntityCount {
  issue_id: number;
  entity_count: number;
}

interface IssuesCountResponse {
  total: number;
  remaining: number;
  issues: Issue[];
}

/**
 * Response shape for `GET /api/entities/placements/users-active`. Matches
 * `UserStoryRailEntry` one-to-one so we can hand rows straight to
 * `<UserStoryAvatar />` without re-mapping.
 */
interface UserStoryRailResponse {
  items: UserStoryRailEntry[];
  scope: "all" | "following";
}

const STORY_WINDOW_HOURS = 168;
const SUGGESTED_LIMIT = 10;
const USER_STORY_RAIL_POLL_MS = 60 * 1000;

/** Exclude compound/influence issues (those with topic_cname set) */
const isSubjectOnly = (issue: Issue): boolean =>
  issue.topic_cname == null || issue.topic_cname === "";

/**
 * Top-of-feed carousel that mixes two kinds of "stories":
 *   1. **Active user stories** — followed users who posted a story
 *      placement in the last 24h. Shown as `UserStoryAvatar` items with
 *      Instagram-style gradient rings (unseen) or gray rings (seen but
 *      re-watchable). Only renders when the viewer is signed in — the
 *      backend scopes by the viewer's follow graph.
 *   2. **Issue stories** — issues in the viewer's subscription set with
 *      recent entity activity, followed by ranked suggestions and an
 *      "Add more" affordance.
 *
 * Anonymous viewers see nothing here. They can still reach individual
 * stories by clicking a user's profile avatar (profile page renders its
 * own story ring via `UserProfilePlate`) or an issue avatar.
 */
const StoryCarousel: React.FC = () => {
  const { currentUser, awaitingUser } = useUser();
  const navigate = useNavigate();
  const [stories, setStories] = useState<Issue[]>([]);
  const [suggestedIssues, setSuggestedIssues] = useState<Issue[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  // Stories v2 powers the user rail. When the flag is off we simply
  // don't fetch / render user stories in the carousel — the issue items
  // below still work exactly as before.
  const { isEnabled: storiesV2Enabled } = useFeatureFlag("stories_v2", true);

  // Fetch the set of followed users with active story placements. The
  // `scope='following'` filter is viewer-dependent and only meaningful
  // when signed in; for anonymous viewers the query is disabled outright
  // (the whole carousel is hidden further down).
  const userStoriesQuery = useQuery<UserStoryRailResponse>({
    queryKey: ["stories", "activeUsers", "following", currentUser?.id ?? null],
    queryFn: async () => {
      const response = await api.get<UserStoryRailResponse>(
        apiRoutes.entities.placementsActiveUsers,
        { params: { scope: "following" } }
      );
      return response.data;
    },
    enabled: Boolean(currentUser) && storiesV2Enabled,
    staleTime: 30_000,
    refetchInterval: USER_STORY_RAIL_POLL_MS,
  });

  // Graceful degradation: if the rail fetch fails we drop the user
  // section entirely and keep showing the issue carousel, rather than
  // tearing down the whole feed. Memoized so dependents (e.g. the
  // `storyContent` useMemo) see a stable array identity across renders.
  const userStoryEntries = useMemo<UserStoryRailEntry[]>(() => {
    if (userStoriesQuery.isError) {
      return [];
    }
    return userStoriesQuery.data?.items ?? [];
  }, [userStoriesQuery.isError, userStoriesQuery.data]);

  /**
   * Build a timestamp for the rolling story window.
   * @returns ISO timestamp for filtering.
   */
  const getStoryWindowStart = (): string => {
    const windowMs = STORY_WINDOW_HOURS * 60 * 60 * 1000;
    return new Date(Date.now() - windowMs).toISOString();
  };

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!currentUser || awaitingUser) {
        if (isMounted) {
          setStories([]);
          setSuggestedIssues([]);
          setError(null);
        }
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Step 1: Fetch issue subscriptions for the signed-in user.
        const subscriptionsRes = await api.get<SubscriptionResponse>(
          apiRoutes.user.subscriptionsByUserId(currentUser.id),
          {
            params: {
              limit: 200,
              sortOrder: "DESC",
            },
          }
        );

        const relations = Array.isArray(subscriptionsRes.data?.relations)
          ? subscriptionsRes.data.relations
          : [];

        const subscribedIssueIds = relations
          .filter(
            (relation) =>
              relation.rel_type === "subscription" &&
              relation.rel_node_type === "issue" &&
              (relation.properties?.value ?? 1) >= 0 &&
              Number.isFinite(relation.rel_node_id)
          )
          .map((relation) => relation.rel_node_id);
        const subscribedIssueSet = new Set(subscribedIssueIds);

        // Step 2: Load stories (subscribed issues with recent activity).
        if (subscribedIssueIds.length > 0) {
          const activityRes = await api.get<IssueEntityCount[]>(
            apiRoutes.entities.root,
            {
              params: {
                countByIssue: true,
                issueIds: subscribedIssueIds.join(","),
                createdAfter: getStoryWindowStart(),
              },
            }
          );
          const counts = Array.isArray(activityRes.data)
            ? activityRes.data.filter(
                (count) =>
                  Number.isFinite(count.issue_id) &&
                  Number.isFinite(count.entity_count) &&
                  count.entity_count > 0
              )
            : [];

          if (counts.length > 0) {
            const activeIssueIds = counts.map((count) => count.issue_id);
            const issuesRes = await api.get<Issue[]>(apiRoutes.issues.root, {
              params: {
                issueIds: JSON.stringify(activeIssueIds),
                limit: activeIssueIds.length,
                sortField: "created_at",
                sortOrder: "DESC",
              },
            });

            const issues = Array.isArray(issuesRes.data) ? issuesRes.data : [];
            const issueMap = new Map(issues.map((issue) => [issue.id, issue]));
            const orderedIssues = counts
              .sort((a, b) => b.entity_count - a.entity_count)
              .map((count) => issueMap.get(count.issue_id))
              .filter((issue): issue is Issue => Boolean(issue));

            if (isMounted) {
              setStories(orderedIssues);
            }
          } else if (isMounted) {
            setStories([]);
          }
        } else if (isMounted) {
          setStories([]);
        }

        // Step 3: Load up to 10 suggested (unsubscribed) issues.
        const rankingsResponse = await getIssueRankings();
        const rankings = Array.isArray(rankingsResponse.data)
          ? rankingsResponse.data
          : [];

        const rankedIssues = rankings
          .map((ranking: IssueRanking) => ranking.issue)
          .filter((issue): issue is Issue => Boolean(issue));

        const rankedSuggested = rankedIssues.filter(
          (issue) => issue && !subscribedIssueSet.has(issue.id)
        );

        const issuePages: Issue[] = [];
        let remaining = 1;
        let offset = 0;
        const limit = 100;
        let pageCount = 0;

        while (remaining > 0 && pageCount < 5) {
          const issuesRes = await api.get<IssuesCountResponse>(apiRoutes.issues.root, {
            params: {
              count: true,
              limit,
              offset,
              sortField: "created_at",
              sortOrder: "DESC",
            },
          });

          const payload = issuesRes.data;
          if (!payload || !Array.isArray(payload.issues)) {
            break;
          }

          issuePages.push(...payload.issues);
          remaining = payload.remaining;
          offset += payload.issues.length;
          pageCount += 1;

          if (payload.issues.length === 0) {
            break;
          }
        }

        const rankedSuggestedIds = new Set(
          rankedSuggested.map((issue) => issue.id)
        );
        const otherSuggested = issuePages.filter(
          (issue) =>
            !subscribedIssueSet.has(issue.id) &&
            !rankedSuggestedIds.has(issue.id)
        );

        const suggestedSubjectOnly = [...rankedSuggested, ...otherSuggested]
          .filter(isSubjectOnly)
          .slice(0, SUGGESTED_LIMIT);

        if (isMounted) {
          setSuggestedIssues(suggestedSubjectOnly);
        }
      } catch (err) {
        if (isMounted) {
          setError("Unable to load stories.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();
    return () => {
      isMounted = false;
    };
  }, [currentUser, awaitingUser, refreshToken]);

  const storyContent = useMemo(() => {
    if (!currentUser || awaitingUser) {
      return null;
    }

    // Show the skeleton only when BOTH sections are still empty — if the
    // user-stories rail has already responded we want it visible
    // immediately, even while the subscriptions-based issue fetch is
    // still working. Mirrors Instagram's "the ring shows up as soon as
    // it's known".
    if (isLoading && userStoryEntries.length === 0) {
      return (
        <Carousel
          className="stories-carousel w-full"
          opts={{ dragFree: true, align: "start" }}
        >
          <CarouselContent className="stories-carousel-content pl-0.5">
            {Array.from({ length: 10 }).map((_, index) => (
              <CarouselItem key={`story-skeleton-${index}`} className="stories-carousel-item basis-auto px-1">
                <LoadingSkeleton
                  avatarSize='h-16 w-16'
                  variant="circle-avatar"
                  containerClassName="stories-carousel-skeleton"
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      );
    }

    // If the issue fetch failed but the user-stories fetch succeeded we
    // still render the rail (issue section just stays empty). Only bail
    // out to the error slot when there is literally nothing to show.
    if (error && userStoryEntries.length === 0) {
      return (
        <div className="stories-carousel-error px-3 py-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
          {error}
        </div>
      );
    }

    return (
      <Carousel
        className="stories-carousel w-full"
        opts={{ dragFree: true, align: "start" }}
      >
        <CarouselContent className="stories-carousel-content pl-0.5">
          {userStoryEntries.map((entry) => (
            <CarouselItem
              key={`user-story-${entry.user_id}`}
              className="stories-carousel-item basis-auto px-1"
            >
              <UserStoryAvatar entry={entry} size="md" />
            </CarouselItem>
          ))}
          {stories.map((issue) => (
            <CarouselItem key={issue.id} className="stories-carousel-item basis-auto px-1">
              <button
                type="button"
                className="stories-carousel-button flex flex-col items-center gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/issue/${issue.cname}/feed`);
                }}
                aria-label={`Open story for ${issue.name}`}
              >
                <EntityAvatar
                  entityType="issue"
                  entity={issue}
                  displayMode="avatarOnly"
                  hasRing={true}
                  className="w-[64px] h-[64px]"
                />
                <span className="stories-carousel-label w-[64px] break-all text-center text-xxs font-medium text-eoe-text dark:text-eoe-dark-text line-clamp-1">
                  {issue.name}
                </span>
              </button>
            </CarouselItem>
          ))}
          {suggestedIssues.map((issue) => (
            <CarouselItem
              key={`suggested-${issue.id}`}
              className="stories-carousel-item basis-auto px-1"
            >
              <div className="stories-carousel-suggested flex flex-col items-center gap-2">
                <div className="stories-carousel-suggested-avatar-wrapper relative">
                  <button
                    type="button"
                    className="stories-carousel-suggested-button block"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/issue/${issue.cname}/feed`);
                    }}
                    aria-label={`View ${issue.name}`}
                  >
                    <EntityAvatar
                      entityType="issue"
                      entity={issue}
                      displayMode="avatarOnly"
                      hasRing={true}
                      className="w-[64px] h-[64px]"
                    />
                  </button>
                  <div
                    className="stories-carousel-suggested-subscribe-inset absolute left-1/2 -translate-x-1/2 top-[calc(100%-20px)] z-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EntitySubscription
                      entityId={issue.id}
                      entityType="issue"
                      variant="default"
                      showLabel={false}
                      className="border border-eoe-icon-hover dark:border-eoe-dark-icon h-6 bg-eoe-bg"
                      onSubscriptionChange={(subscribed) => {
                        if (subscribed) {
                          setSuggestedIssues((prev) =>
                            prev.filter((s) => s.id !== issue.id)
                          );
                        }
                      }}
                    />
                  </div>
                </div>
                <span className="stories-carousel-label w-[64px] break-all text-center text-xxs font-medium text-eoe-text dark:text-eoe-dark-text line-clamp-1">
                  {issue.name}
                </span>
              </div>
            </CarouselItem>
          ))}
          <CarouselItem key="stories-add-more" className="stories-carousel-item basis-auto px-1">
            <button
              type="button"
              className="stories-carousel-add-button flex flex-col items-center gap-2"
              onClick={() => setIsModalOpen(true)}
              aria-label="Add more subscriptions"
            >
              <div className="stories-carousel-add-circle flex items-center justify-center w-[64px] h-[64px] rounded-full border-[3px] border-eoe-icon-hover dark:border-eoe-dark-icon text-eoe-text dark:text-eoe-dark-text">
                <Plus className="size-5" />
              </div>
              <span className="stories-carousel-add-label w-[64px] text-center text-xxs font-medium text-eoe-text dark:text-eoe-dark-text line-clamp-1">
                Add more
              </span>
            </button>
          </CarouselItem>
        </CarouselContent>
      </Carousel>
    );
  }, [
    currentUser,
    awaitingUser,
    isLoading,
    error,
    stories,
    suggestedIssues,
    userStoryEntries,
    navigate,
  ]);

  if (!storyContent) {
    return null;
  }

  return (
    <div className="stories-carousel-wrapper w-full pt-2">
      {storyContent}
      <SubscriptionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubscriptionChange={() => setRefreshToken((prev) => prev + 1)}
      />
    </div>
  );
};

export default StoryCarousel;
